import React, { useState } from 'react';
import Layout from './components/Layout/Layout';
import WelcomePanel from './components/WelcomePanel';
import VisualEditor from './components/VisualEditor/VisualEditor';
import JsonEditor from './components/JsonEditor/JsonEditor';
import Preview from './components/Preview/Preview';
import ActionInspector from './components/ActionInspector/ActionInspector';
import { ProjectProvider } from './context/ProjectContext';
import ExecutionHistory from './components/ExecutionHistory/ExecutionHistory';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Auth/Login';
import AppCatalog from './components/Published/AppCatalog';
import InstalledApp from './components/InstalledApps/InstalledApp';
import PublishedApp from './components/Published/PublishedApp';
import UserManagement from './components/Admin/UserManagement';
import Publishing from './components/Admin/Publishing';
import FirstRunSetup from './components/Auth/FirstRunSetup';
import Projects from './components/Projects/Projects';
import {BrowserIdentityProvider, usePageIdentity} from './components/Identity/BrowserIdentity';
import {NAV_ITEMS} from './components/Layout/navItems';

function AppContent(): React.ReactElement {
  const [activeItem, setActiveItem] = useState<string>('');
  usePageIdentity(NAV_ITEMS.find(item=>item.id===activeItem)?.label);
  const [publishingProjectId, setPublishingProjectId] = useState<number>();
  const navigate = (id: string) => {setPublishingProjectId(undefined); setActiveItem(id);};

  function renderContent(): React.ReactElement | null {
    if (!activeItem) return <WelcomePanel onNavigate={navigate} />;
    if (activeItem === 'visual-editor') return <VisualEditor />;
    if (activeItem === 'projects') return <Projects onOpen={navigate} onPublish={id => {setPublishingProjectId(id); setActiveItem('publishing');}} />;
    if (activeItem === 'json-editor') return <JsonEditor />;
    if (activeItem === 'preview') return <Preview />;
    if (activeItem === 'inspector') return <ActionInspector />;
    if (activeItem === 'history') return <ExecutionHistory />;
    if (activeItem === 'publishing') return <Publishing initialProjectId={publishingProjectId} />;
    if (activeItem === 'users') return <UserManagement />;
    return null;
  }

  return (
    <Layout activeItem={activeItem} onNavigate={navigate}>
      {renderContent()}
    </Layout>
  );
}

// ProjectProvider wraps the entire app so all editor views share one context.
function AuthenticatedApp(): React.ReactElement {
  const { user, loading, setupRequired } = useAuth();
  const publishedMatch = window.location.pathname.match(/^\/apps\/([^/]+)(?:\/([^/]+))?\/?$/);
  if (publishedMatch) return <ProjectProvider><PublishedApp slug={decodeURIComponent(publishedMatch[1])} pageSlug={publishedMatch[2] ? decodeURIComponent(publishedMatch[2]) : undefined}/></ProjectProvider>;
  if (loading) return <p style={{padding:32}}>Loading…</p>;
  if (setupRequired) return <FirstRunSetup />;
  if (!user) return <Login />;
  if (window.location.pathname !== '/') return <InstalledApp fallback={user.role==='user'?<AppCatalog/>:<ProjectProvider><AppContent/></ProjectProvider>}/>;
  if (user.role === 'user') return <AppCatalog />;
  return (
    <ProjectProvider>
      <AppContent />
    </ProjectProvider>
  );
}

function App(): React.ReactElement { return <AuthProvider><BrowserIdentityProvider><AuthenticatedApp /></BrowserIdentityProvider></AuthProvider>; }

export default App;
